import React from "react";
import PropTypes from "prop-types";
import {I18n} from "aws-amplify/utils";
import {Box, InputAdornment, List, Popper, TextField} from "@mui/material";
import {Clear, Search} from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import ListItemButton from "@mui/material/ListItemButton";
import {getDetailedAccounts} from "../../../graphql/queries";
import GraphQlTool from "../../../Utils/GraphQlTool";
import './AccountSelector.css';
import {filterByName} from "../SearchBar/AccountFilteringLogic";

const defaultName = "MONNAYEUR";
/** @type {AccountSelector} */
let selectorInstance;

/**
 * @typedef {{ id: number, name: string }} AccountOption
 */

/**
 * @typedef {Object} AccountSelectorProps
 * @property {object} user Dashboard user used to load accounts.
 * @property {number} user.accountId
 * @property {{ graphql: Function }} API
 * @property {AccountOption[]} [accounts]
 * @property {number} [selectedAccountID]
 * @property {string} [placeholder]
 * @property {boolean} [isDarkStyle]
 * @property {boolean} [showIcon]
 * @property {(event: React.MouseEvent, accountId: number) => void} [onChange]
 */

/**
 * Recherche et sélectionne un compte parmi ceux accessibles à l'utilisateur.
 * @extends {React.Component<AccountSelectorProps>}
 */
export default class AccountSelector extends React.Component {
	static defaultProps = {
		selectedAccountID: 0,
		placeholder: '',
		isDarkStyle: false,
		showIcon: false,
		onChange: () => {}
	};

	static propTypes = {
		selectedAccountID: PropTypes.number.isRequired,
		placeholder: PropTypes.string.isRequired,
		isDarkStyle: PropTypes.bool.isRequired,
		showIcon: PropTypes.bool,
		onChange: PropTypes.func
	};

	constructor( props ) {
		super( props );
		selectorInstance = this;
		this.containerReference = React.createRef();
		this.listReference = React.createRef();
		this.inputReference = React.createRef();
		this.state = {
			searchValue: '',
			highlightedIndex: -1,
			isOpened: false,
			filteredAccounts: /** @type {Array<{ id: number, name: string }>} */ ( [] )
		};
	}

	loadAccounts() {
		if( this.props.API && this.props.user?.accountId ) {
			this.props.API
				.graphql( { query: getDetailedAccounts, variables: { accountId: this.props.user?.accountId }} )
				.then( returned => {
					const tool = new GraphQlTool( 'getDetailedAccounts' , returned );
					this.setState( { accounts: tool.extract() } );
				})
				.catch( ( error ) => {
					console.error( "error" , error );
				})
		}
	}

	handleClickOutOfTheZone( event ) {
		const clickedInContainer = selectorInstance.containerReference?.current?.contains( event.target );
		const clickedInList = selectorInstance.listReference?.current?.contains( event.target );
		if ( ! clickedInContainer && ! clickedInList) {
			selectorInstance.setState({ isOpened: false, highlightedIndex: -1 });
		}
	}

	clearAllFiltersIfNoSearch() {
		if( selectorInstance.state.searchValue?.trim() === '' &&
			selectorInstance.state.filteredAccounts !== selectorInstance.props.accounts ) {
			selectorInstance.setState( {
				filteredAccounts: selectorInstance.props.accounts,
				isOpened: false,
				highlightedIndex: -1
			} , () => { selectorInstance.inputReference?.current?.focus() } );
			return true;
		}
		return false;
	}

	/**
	 * @param {React.MouseEvent} event
	 * @param {object} account
	 * @param {number} account.id
	 * @param {string} account.name
	 */
	handleUserSelection( event , account ) {
		if( selectorInstance.props.onChange )
			selectorInstance.props.onChange( event ,account.id );
		selectorInstance.setState( {
			searchValue: account.name,
			highlightedIndex: -1,
			isOpened: false
		} );
	}

	clearSearch() {
		selectorInstance.setState( { searchValue: '' } , () => { selectorInstance.inputReference?.current?.focus() } );
	}

	componentDidMount() {
		document.addEventListener( 'pointerdown', this.handleClickOutOfTheZone );
	}

	componentWillUnmount() {
		document.removeEventListener( 'pointerdown', this.handleClickOutOfTheZone );
	}

	renderSearchBar() {
		return (
			<TextField
				key={`account-selector-text-field`}
				ref={ selectorInstance.containerReference }
				inputRef={ selectorInstance.inputReference }
				label={ I18n.get('Filter by names') }
				className="searchbar-textField"
				InputProps={{
					startAdornment: (
						<InputAdornment position="start">
							<Search />
						</InputAdornment>
					),
					endAdornment: (
						<InputAdornment position="end">
							<IconButton
								size="small"
								edge="end"
								onClick={ selectorInstance.clearSearch }
							>
								<Clear fontSize="small" />
							</IconButton>
						</InputAdornment>
					),
				}}
				size={ "medium" }
				variant={ "outlined" }
				placeholder={ selectorInstance.props.placeholder }
				value={ selectorInstance.state.searchValue }
				onChange={ event => selectorInstance.setState( { searchValue: event.target.value } ) }
				onFocus={ () => selectorInstance.setState( { isOpened: true } ) }
				autoComplete={ "off" }
			/>
		);
	}

	renderFiltered() {
		const popperPlacement = "bottom-start";
		const paperElevation = 3;
		const theme = selectorInstance.props.isDarkStyle ? "dark" : "";
		const classes = {
			popper: `filtered-popper`,
			paper: `filtered-popper`,
			list: `filtered-list`,
			listItem: `filtered-list-item ${theme}`,
			listItemIcon: `filtered-list-item-icon ${theme}`,
			boxTitle: `filtered-box-title ${theme}`,
			boxSubTitle: `filtered-box-subtitle ${theme}`
		};

		if( ! selectorInstance.containerReference.current ) return null;

		return (
			<Popper
				open={ selectorInstance.state.isOpened }
				anchorEl={ selectorInstance.containerReference.current }
				placement={ popperPlacement }
				className={ classes.popper } style={{ zIndex: 1500 }} >
				<Paper
					elevation={ paperElevation }
					className={ classes.paper } >
					<List
						ref={ selectorInstance.listReference }
						dense
						className={ classes.list }
						autoFocus={false}
					>
						{selectorInstance.renderAccounts( classes )}
					</List>
				</Paper>
			</Popper>
		);
	}

	renderAccounts( classes ) {
		return(
			<>
				{ selectorInstance.state.filteredAccounts?.map( ( account, index ) => (
					<ListItemButton
						className={ classes.listItem }
						key={ account.id }
						selected={ index === selectorInstance.state.highlightedIndex }
						autoFocus={false}
						onClick={ evt => selectorInstance.handleUserSelection( evt , account ) }
					>
						<Box>
							<div className={ classes.boxTitle }>{ account.name }</div>
						</Box>
					</ListItemButton>
				))}
			</>
		);
	}

	render() {
		if( ! this.state.accounts ) {
			this.loadAccounts();
			return null;
		}

		if( this.clearAllFiltersIfNoSearch() ) {
			return null;
		} else {
			const filtered = filterByName( this.state.accounts , this.state.searchValue );
			const isFocused = document.activeElement === this.inputReference.current;
			if( ( this.state.searchValue && isFocused ) && this.state.filteredAccounts?.length !== filtered?.length ) {
				this.setState( {
					filteredAccounts: filtered,
					isOpened: filtered.length > 0 /*&& isFocused*/,
					highlightedIndex: -1
				} , () => { this.inputReference?.current?.focus() } );
				return null;
			} else {
				return (
					<div>
						{ this.renderSearchBar() }
						{ this.renderFiltered() }
					</div>
				);
			}
		}
	}
}
